import { ref } from 'vue'
import { BASE_URL } from "@/composables/url_config"
import { log } from "@/composables/useLog"
import axios from "axios"

const error = ref(null)

// Helper function for headers
const getHeaders = (token) => ({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
    "Accept": "application/json"
})

// ========== Get Port Requests List (Admin) ==========
export const getPortRequestsList = async (token, params = {}) => {
    log("getPortRequestsList called with:", params)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/get_port_requests_list_api`
        const result = await axios.get(url, {
            headers: getHeaders(token),
            params: params
        })
        log("getPortRequestsList result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in getPortRequestsList:")
        log(err)
        return { error: true, message: "Error fetching port requests" }
    }
}

// ========== Get Port Request Details ==========
export const getPortRequestDetails = async (token, portRequestId) => {
    log("getPortRequestDetails called for:", portRequestId)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/get_port_request_details_api`
        const result = await axios.get(url, {
            headers: getHeaders(token),
            params: { port_request_id: portRequestId }
        })
        log("getPortRequestDetails result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in getPortRequestDetails:")
        log(err)
        return { error: true, message: "Error fetching port request details" }
    }
}

// ========== Get Port Request Options ==========
export const getPortRequestOptions = async (token) => {
    log("getPortRequestOptions called")
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/get_port_request_options_api`
        const result = await axios.get(url, { headers: getHeaders(token) })
        log("getPortRequestOptions result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in getPortRequestOptions:")
        log(err)
        return { error: true, message: "Error fetching options" }
    }
}

// ========== Update Port Request Status ==========
export const updatePortRequestStatus = async (token, portRequestId, statusId) => {
    log("updatePortRequestStatus called for:", portRequestId, statusId)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/update_port_request_status_api`
        const data = {
            port_request_id: portRequestId,
            status_id: statusId
        }
        const result = await axios.put(url, data, { headers: getHeaders(token) })
        log("updatePortRequestStatus result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in updatePortRequestStatus:")
        log(err)
        if (err.response?.data?.message) {
            return { error: true, message: err.response.data.message }
        }
        return { error: true, message: "Error updating status" }
    }
}

// ========== EndUser Portability Functions ==========

// Send verification code for portability request
export const sendPortabilityVerificationCode = async (token, msisdn, type = 'destination') => {
    log("sendPortabilityVerificationCode called for:", msisdn, type)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/send_portability_verification_code_api`
        const data = { msisdn, type }
        const result = await axios.post(url, data, { headers: getHeaders(token) })
        log("sendPortabilityVerificationCode result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in sendPortabilityVerificationCode:")
        log(err)
        if (err.response?.data?.message) {
            return { error: true, message: err.response.data.message }
        }
        return { error: true, message: "Error sending verification code" }
    }
}

// Verify code for portability request
export const verifyPortabilityCode = async (token, msisdn, code, type = 'destination') => {
    log("verifyPortabilityCode called for:", msisdn, type)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/verify_portability_code_api`
        const data = { msisdn, code, type }
        const result = await axios.post(url, data, { headers: getHeaders(token) })
        log("verifyPortabilityCode result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in verifyPortabilityCode:")
        log(err)
        if (err.response?.data?.message) {
            return { error: true, message: err.response.data.message }
        }
        return { error: true, message: "Error verifying code" }
    }
}

// Create port request by EndUser
export const createEndUserPortRequest = async (token, data) => {
    log("createEndUserPortRequest called with:", data)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/create_enduser_port_request_api`
        const result = await axios.post(url, data, { headers: getHeaders(token) })
        log("createEndUserPortRequest result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in createEndUserPortRequest:")
        log(err)
        if (err.response?.data?.message) {
            return { error: true, message: err.response.data.message }
        }
        return { error: true, message: "Error creating port request" }
    }
}

// Upload documents for port request (multipart/form-data)
export const uploadPortRequestDocuments = async (token, portRequestId, files) => {
    log("uploadPortRequestDocuments called for:", portRequestId)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/upload_port_request_documents_api`
        const formData = new FormData()
        formData.append('port_request_id', portRequestId)
        if (files.id_document) formData.append('id_document', files.id_document)
        if (files.last_invoice) formData.append('last_invoice', files.last_invoice)
        if (files.power_of_attorney) formData.append('power_of_attorney', files.power_of_attorney)

        const result = await axios.post(url, formData, {
            headers: {
                "Authorization": `Bearer ${token}`
            }
        })
        log("uploadPortRequestDocuments result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in uploadPortRequestDocuments:")
        log(err)
        if (err.response?.data?.message) {
            return { error: true, message: err.response.data.message }
        }
        return { error: true, message: "Error uploading documents" }
    }
}

// Get EndUser's own port requests
export const getMyPortRequests = async (token) => {
    log("getMyPortRequests called")
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/get_my_port_requests_api`
        const result = await axios.get(url, { headers: getHeaders(token) })
        log("getMyPortRequests result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in getMyPortRequests:")
        log(err)
        return { error: true, message: "Error fetching port requests" }
    }
}

const usePortRequestsAPI = () => {
    return {
        error,
        getPortRequestsList,
        getPortRequestDetails,
        getPortRequestOptions,
        updatePortRequestStatus,
        sendPortabilityVerificationCode,
        verifyPortabilityCode,
        createEndUserPortRequest,
        uploadPortRequestDocuments,
        getMyPortRequests
    }
}

export default usePortRequestsAPI
